import React, { useId } from 'react';
import isNil from 'lodash/isNil';
import cn from 'classnames';
import { PredictedPaiParams, PublishedParams, PredictedIktosParams } from '@Pages/RetroSynthesis/constants';
import PropTypes from 'prop-types';
import InnerHTML from '@Components/InnerHTML';
import { Card } from '@els/els-react--card';
import { isIktosPredictiveRetrosynthesisEnabled, isPaiPredictiveRetrosynthesisEnabled } from '../../predictiveUtils';
import { paramsToString, buildIktosParameters, buildPaiParameters } from './projectParametersUtils';
import IntermediatesParameter from './IntermediatesParameter';

import './ProjectParametersGridItem.scss';

export function ParamItem({ paramName, value, paramType, params, isPredicted, step }) {
  let text = paramsToString(paramName, value, isPredicted, params);
  let isHighlighted = false;
  if (isPredicted && step) {
    isHighlighted = params?.highlightedParams && params.highlightedParams.includes(paramName);
    if (isHighlighted) text = `<b>*${text}</b>`;
  }

  return !isNil(value) ? <InnerHTML className={`param ${paramType}-param param--${paramName}`} tag="div" html={text} /> : null;
}

ParamItem.propTypes = {
  paramName: PropTypes.string,
  value: PropTypes.any,
  paramType: PropTypes.string,
  params: PropTypes.object,
  isPredicted: PropTypes.bool,
  step: PropTypes.number,
};

function isIktosSubstructureParam(paramName) {
  return paramName === PredictedIktosParams.INCLUDE_SUBSTRUCTURES || paramName === PredictedIktosParams.EXCLUDE_SUBSTRUCTURES;
}

function filterIktosParam(paramName, predictedParametersUpdated) {
  if (paramName === PredictedIktosParams.INCLUDE_SUBSTRUCTURES) return true;
  if (paramName === PredictedIktosParams.EXCLUDE_SUBSTRUCTURES) return !isNil(predictedParametersUpdated[paramName]);
  return !isNil(predictedParametersUpdated[paramName]);
}

function renderIktosParamItem(paramName, predictedParametersUpdated, step) {
  if (isIktosSubstructureParam(paramName)) {
    return (
      <li key={`predicted-param-${paramName}`}>
        <IntermediatesParameter smiles={predictedParametersUpdated[paramName]} paramName={paramName} />
      </li>
    );
  }
  return (
    <li key={`predicted-param-${paramName}`}>
      <ParamItem
        paramName={paramName}
        value={predictedParametersUpdated[paramName]}
        paramType="predicted"
        params={predictedParametersUpdated}
        isPredicted
        step={step}
      />
    </li>
  );
}

function preparePredictedParameters(predictedParameters, predictiveIktosEnabled, predictivePAIEnabled) {
  if (predictiveIktosEnabled && Object.keys(predictedParameters).length) {
    return buildIktosParameters({ ...predictedParameters });
  }
  if (predictivePAIEnabled) {
    return buildPaiParameters({ ...predictedParameters });
  }
  return { ...predictedParameters };
}

function ParametersCard({ title, children }) {
  const headingId = useId();

  return (
    <Card
      className="project-parameters-card"
      role="group"
      aria-labelledby={headingId}
      heading={<span id={headingId}>{title}</span>}
      headingLevel="h3"
      fill>
      <ul className="params-bunch">{children}</ul>
    </Card>
  );
}

ParametersCard.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

export function ProjectParametersGridItem({ publishedParameters = {}, predictedParameters = {}, step }) {
  const predictiveIktosEnabled = isIktosPredictiveRetrosynthesisEnabled();
  const predictivePAIEnabled = !predictiveIktosEnabled && isPaiPredictiveRetrosynthesisEnabled();
  const predictedParametersUpdated = preparePredictedParameters(predictedParameters, predictiveIktosEnabled, predictivePAIEnabled);
  let predictedParameterItems = [];
  if (predictivePAIEnabled) {
    predictedParameterItems = PredictedPaiParams?.allTypes
      .filter((paramName) => !isNil(predictedParametersUpdated[paramName]))
      .map((paramName) => (
        <li key={`predicted-param-${paramName}`}>
          <ParamItem
            paramName={paramName}
            value={predictedParametersUpdated[paramName]}
            paramType="predicted"
            params={predictedParametersUpdated}
            isPredicted
            step={step}
          />
        </li>
      ));
  } else if (predictiveIktosEnabled && Object.keys(predictedParametersUpdated).length > 0) {
    predictedParameterItems = PredictedIktosParams?.allTypes
      .filter((paramName) => filterIktosParam(paramName, predictedParametersUpdated))
      .map((paramName) => renderIktosParamItem(paramName, predictedParametersUpdated, step));
  }
  const publishedParameterItems = PublishedParams?.allTypes
    .filter((paramName) => !isNil(publishedParameters[paramName]))
    .map((paramName) => (
      <li key={`published-param-${paramName}`}>
        <ParamItem
          paramName={paramName}
          value={publishedParameters[paramName]}
          paramType="published"
          params={publishedParameters}
          isPredicted={false}
        />
      </li>
    ));
  const hasPredictedParameters = predictedParameterItems.length > 0;
  const hasPublishedParameters = publishedParameterItems.length > 0;

  return (
    <div
      className={cn('project-parameters-grid-item', {
        'project-parameters-grid-item--published-only': !hasPredictedParameters && hasPublishedParameters,
      })}>
      {hasPredictedParameters && <ParametersCard title="Predicted">{predictedParameterItems}</ParametersCard>}
      {hasPublishedParameters && <ParametersCard title="Published">{publishedParameterItems}</ParametersCard>}
    </div>
  );
}

export default ProjectParametersGridItem;
